import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Plus, Edit, Trash2, Truck } from 'lucide-react';
import PublicRecordFields from './PublicRecordFields';
import NameplateSettings from './NameplateSettings';
import LocationTransferDialog from './LocationTransferDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EditableList = ({ title, items, onAdd, onEdit, onDelete, columns, testIdPrefix }) => {
  const { t } = useTranslation();
  const [addOpen, setAddOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState({});

  const openCreate = () => { setForm({}); setEditItem(null); setAddOpen(true); };
  const openEdit = (item) => { setForm({ name: item.name, description: item.description || '', sort_order: item.sort_order ?? 0 }); setEditItem(item); setAddOpen(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim()) { toast.error(t('inv.settings.nameRequired')); return; }
    try {
      if (editItem) { await onEdit(editItem.id, form); toast.success(t('inv.settings.updated')); }
      else { await onAdd(form); toast.success(t('inv.settings.added')); }
      setAddOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try { await onDelete(deleteId); toast.success(t('inv.settings.deletedToast')); setDeleteId(null); }
    catch (err) { toast.error(err.response?.data?.detail || t('inv.common.deleteFailed')); }
  };

  return (
    <Card data-testid={`${testIdPrefix}-card`}>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-base">{title} ({items.length})</CardTitle>
        <Button size="sm" onClick={openCreate} data-testid={`add-${testIdPrefix}-btn`}><Plus className="w-4 h-4 mr-1" /> Add</Button>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">{t('inv.settings.noItems')}</p>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>{t('common.name')}</TableHead>
              {columns?.includes('description') && <TableHead>{t('inv.common.description')}</TableHead>}
              {columns?.includes('sort_order') && <TableHead className="w-[80px]">{t('inv.settings.order')}</TableHead>}
              <TableHead className="w-[80px]">{t('common.actions')}</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} data-testid={`${testIdPrefix}-row-${item.name}`}>
                  <TableCell className="font-medium">{item.name}</TableCell>
                  {columns?.includes('description') && <TableCell className="text-sm text-muted-foreground">{item.description || '-'}</TableCell>}
                  {columns?.includes('sort_order') && <TableCell className="text-sm">{item.sort_order}</TableCell>}
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(item)} data-testid={`edit-${testIdPrefix}-${item.name}`}><Edit className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => setDeleteId(item.id)} data-testid={`delete-${testIdPrefix}-${item.name}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid={`${testIdPrefix}-form-dialog`}>
          <DialogHeader><DialogTitle>{editItem ? t('common.edit') : t('common.add')} — {title}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div><Label className="text-xs">Name *</Label><Input value={form.name || ''} onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value  }))} required data-testid={`${testIdPrefix}-name-input`} /></div>
            {columns?.includes('description') && <div><Label className="text-xs">{t('inv.common.description')}</Label><Input value={form.description || ''} onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value  }))} /></div>}
            {columns?.includes('sort_order') && <div><Label className="text-xs">{t('inv.settings.sortOrder')}</Label><Input type="number" value={form.sort_order ?? 0} onChange={(e) => setForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0  }))} /></div>}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)} data-testid="btn-cancel">{t('common.cancel')}</Button>
              <Button type="submit" data-testid={`save-${testIdPrefix}-btn`}>{editItem ? t('inv.common.update') : t('common.add')}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>{t('inv.settings.deleteTitle')}</AlertDialogTitle><AlertDialogDescription>{t('inv.settings.deleteHint')}</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">{t('common.delete')}</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

const InventorySettings = ({
  categories, conditions, manufacturers = [],
  inspectionTypes = [], damageSeverities = [], repairStatuses = [], damageAffected = [],
  storageLocations = [],
  onCategoriesChange, onConditionsChange, onManufacturersChange,
  onInspectionTypesChange, onDamageSeveritiesChange, onRepairStatusesChange, onDamageAffectedChange,
  onStorageLocationsChange,
}) => {
  const { t } = useTranslation();
  const [transferOpen, setTransferOpen] = useState(false);
  return (
  <div className="space-y-6" data-testid="inventory-settings">
    <NameplateSettings />

    <PublicRecordFields />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <EditableList title={t('inv.settings.categories')} items={categories} columns={['description']} testIdPrefix="category"
        onAdd={async (d) => { await axios.post(`${API}/inventory/categories`, d); onCategoriesChange(); }}
        onEdit={async (id, d) => { await axios.put(`${API}/inventory/categories/${id}`, d); onCategoriesChange(); }}
        onDelete={async (id) => { await axios.delete(`${API}/inventory/categories/${id}`); onCategoriesChange(); }}
      />
      <EditableList title={t('inv.settings.conditions')} items={conditions} columns={['description', 'sort_order']} testIdPrefix="condition"
        onAdd={async (d) => { await axios.post(`${API}/inventory/conditions`, d); onConditionsChange(); }}
        onEdit={async (id, d) => { await axios.put(`${API}/inventory/conditions/${id}`, d); onConditionsChange(); }}
        onDelete={async (id) => { await axios.delete(`${API}/inventory/conditions/${id}`); onConditionsChange(); }}
      />
      <EditableList title={t('inv.settings.manufacturers')} items={manufacturers} columns={['description']} testIdPrefix="manufacturer"
        onAdd={async (d) => { await axios.post(`${API}/inventory/manufacturers`, d); onManufacturersChange(); }}
        onEdit={async (id, d) => { await axios.put(`${API}/inventory/manufacturers/${id}`, d); onManufacturersChange(); }}
        onDelete={async (id) => { await axios.delete(`${API}/inventory/manufacturers/${id}`); onManufacturersChange(); }}
      />
      <EditableList title={t('inv.settings.inspectionTypes')} items={inspectionTypes} columns={['sort_order']} testIdPrefix="inspection-type"
        onAdd={async (d) => { await axios.post(`${API}/inventory/config/inspection_types`, d); onInspectionTypesChange(); }}
        onEdit={async (id, d) => { await axios.put(`${API}/inventory/config/inspection_types/${id}`, d); onInspectionTypesChange(); }}
        onDelete={async (id) => { await axios.delete(`${API}/inventory/config/inspection_types/${id}`); onInspectionTypesChange(); }}
      />
      <EditableList title={t('inv.settings.damageSeverities')} items={damageSeverities} columns={['sort_order']} testIdPrefix="damage-severity"
        onAdd={async (d) => { await axios.post(`${API}/inventory/config/damage_severities`, d); onDamageSeveritiesChange(); }}
        onEdit={async (id, d) => { await axios.put(`${API}/inventory/config/damage_severities/${id}`, d); onDamageSeveritiesChange(); }}
        onDelete={async (id) => { await axios.delete(`${API}/inventory/config/damage_severities/${id}`); onDamageSeveritiesChange(); }}
      />
      <EditableList title={t('inv.settings.repairStatuses')} items={repairStatuses} columns={['sort_order']} testIdPrefix="repair-status"
        onAdd={async (d) => { await axios.post(`${API}/inventory/config/repair_statuses`, d); onRepairStatusesChange(); }}
        onEdit={async (id, d) => { await axios.put(`${API}/inventory/config/repair_statuses/${id}`, d); onRepairStatusesChange(); }}
        onDelete={async (id) => { await axios.delete(`${API}/inventory/config/repair_statuses/${id}`); onRepairStatusesChange(); }}
      />
      <EditableList title={t('inv.settings.damageAffected')} items={damageAffected} columns={['sort_order']} testIdPrefix="damage-affected"
        onAdd={async (d) => { await axios.post(`${API}/inventory/config/damage_affected`, d); onDamageAffectedChange?.(); }}
        onEdit={async (id, d) => { await axios.put(`${API}/inventory/config/damage_affected/${id}`, d); onDamageAffectedChange?.(); }}
        onDelete={async (id) => { await axios.delete(`${API}/inventory/config/damage_affected/${id}`); onDamageAffectedChange?.(); }}
      />
      {/* Where a tool physically lives: van, shelf, container. Kept as a list
          so "Van 3" does not become "van3" and "Van three". */}
      <EditableList title={t('inv.settings.storageLocations')} items={storageLocations} columns={['description', 'sort_order']} testIdPrefix="storage-location"
        onAdd={async (d) => { await axios.post(`${API}/inventory/config/storage_locations`, d); onStorageLocationsChange?.(); }}
        onEdit={async (id, d) => { await axios.put(`${API}/inventory/config/storage_locations/${id}`, d); onStorageLocationsChange?.(); }}
        onDelete={async (id) => { await axios.delete(`${API}/inventory/config/storage_locations/${id}`); onStorageLocationsChange?.(); }}
      />
      <div className="-mt-2">
        <Button variant="outline" size="sm" onClick={() => setTransferOpen(true)} data-testid="settings-transfer-location-btn">
          <Truck className="w-4 h-4 mr-1" /> {t('inv.transfer.button')}
        </Button>
        {transferOpen && (
          <LocationTransferDialog
            open={transferOpen}
            onOpenChange={setTransferOpen}
            locations={storageLocations}
            onDone={() => onStorageLocationsChange?.()}
          />
        )}
      </div>
    </div>
  </div>
  );
};

export default InventorySettings;
